import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { PageContext } from '@/types/pageContext';
import { contextExtractionService } from '@/services/contextExtractionService';

export const usePageContext = () => {
  const [pageContext, setPageContext] = useState<PageContext | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();

  const extractContext = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Small delay to ensure DOM is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const context = contextExtractionService.extractPageContext();
      setPageContext(context);
    } catch (err) {
      console.error('Error extracting page context:', err);
      setError('Falha ao extrair contexto da página');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Extract context when location changes
  useEffect(() => {
    const timeoutId = setTimeout(extractContext, 500); // Debounce to avoid excessive calls
    return () => clearTimeout(timeoutId);
  }, [location.pathname, location.search, extractContext]);

  // Re-extract context when DOM changes significantly
  useEffect(() => {
    let observer: MutationObserver;
    
    const startObserving = () => {
      observer = new MutationObserver((mutations) => {
        const significantChange = mutations.some(mutation => 
          mutation.type === 'childList' && 
          mutation.addedNodes.length > 0 && 
          Array.from(mutation.addedNodes).some(node => 
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).children.length > 0
          )
        );
        
        if (significantChange) {
          // Debounce the context extraction
          const timeoutId = setTimeout(extractContext, 1000);
          return () => clearTimeout(timeoutId);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false
      });
    };

    // Start observing after initial load
    const timeoutId = setTimeout(startObserving, 2000);
    
    return () => {
      clearTimeout(timeoutId);
      observer?.disconnect();
    };
  }, [extractContext]);

  const refreshContext = useCallback(() => {
    extractContext();
  }, [extractContext]);

  return {
    pageContext,
    isLoading,
    error,
    refreshContext
  };
};